import React, { useEffect, useState, useCallback } from 'react';
import { Form, Input, Button, Select, App, Checkbox } from 'antd';
import { SparkPlugItem } from '../../types/ProductItems';
import RefOemManager from '../common/RefOemManager';
import ImageUploader from '../common/ImageUploader';
import { toBase64, mapBase64ToUploadList } from '../../utils/formsThumbnails';
import { fetchProductRefsOems } from '../../services/productAPI';

interface SparkPlugFormProps {
  onAdd: (item: SparkPlugItem) => void;
  initialValues?: SparkPlugItem | null;
}
const { Option } = Select;

const nonNegativeRule = (label: string) => ({
  validator(_: any, value: any) {
    if (value === undefined || value === '' || Number(value) >= 0) return Promise.resolve();
    return Promise.reject(new Error(`${label} не может быть отрицательным`));
  },
});

const SparkPlugForm: React.FC<SparkPlugFormProps> = ({ onAdd, initialValues }) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [refList, setRefList] = useState<{ [brand: string]: string[] }>({});
  const [oemList, setOemList] = useState<{ [model: string]: string[] }>({});

  // Калильное ACDelco (владелец 12.10). ACDelco пишет калильное по своей шкале,
  // источники не сходятся — у таких свечей поле не заполняем, а витрина вместо
  // «—» пишет «сверьте по аналогам». Флажок выключает поле и снимает с него
  // обязательность. Отдельной колонки в базе нет: флажок — это «ACDelco с пустым
  // калильным», поэтому при открытии такой карточки он уже стоит.
  const brand = Form.useWatch('brand', form);
  const [heatByCross, setHeatByCross] = useState(false);

  const resetForm = useCallback(() => {
    form.resetFields();
    setRefList({});
    setOemList({});
    setHeatByCross(false);
  }, [form]);

  // Сменили марку с ACDelco на другую — флажок снимается, поле снова обязательно.
  useEffect(() => {
    if (brand !== 'ACDelco') setHeatByCross(false);
  }, [brand]);

  const toggleHeatByCross = (on: boolean) => {
    setHeatByCross(on);
    if (on) form.setFieldsValue({ heatRating: '' });
    form.validateFields(['heatRating']).catch(() => undefined);
  };

  // Владелец 12.10: выбрали в списке марку ACDelco — флажок включается сам.
  // Только по выбору в списке, не при открытии карточки: если у какой-то
  // ACDelco калильное всё же вписано, открытие его не сотрёт.
  const handleBrandSelect = (value: string) => {
    if (value === 'ACDelco') toggleHeatByCross(true);
  };

  useEffect(() => {
    let mounted = true;
    if (initialValues) {
      const { images = [], ...rest } = initialValues;
      form.setFieldsValue({
        ...rest,
        images: mapBase64ToUploadList(images),
      });
      setRefList(initialValues.references ?? {});
      setOemList(initialValues.oems ?? {});
      setHeatByCross(initialValues.brand === 'ACDelco' && String(initialValues.heatRating ?? '').trim() === '');
      const mysqlId = Number(initialValues.id);
      if (Number.isInteger(mysqlId) && mysqlId > 0) {
        fetchProductRefsOems('spark_plugs', mysqlId).then(fresh => {
          if (fresh && mounted) {
            setRefList(fresh.references);
            setOemList(fresh.oems);
          }
        });
      }
    } else {
      resetForm();
    }
    return () => { mounted = false; };
  }, [initialValues, form, resetForm]);

  const handleFinish = useCallback(
    async (values: any) => {
      try {
        const imagesField = form.getFieldValue('images') || [];
        const base64Images = await Promise.all(
          imagesField.map(async (file: any) =>
            file?.originFileObj ? await toBase64(file.originFileObj) : file?.url || file?.thumbUrl
          )
        );

        const originalServerUrls = (initialValues?.images ?? [])
          .filter((img: string) => typeof img === 'string' && !img.startsWith('data:'));
        const _deletedImgs = originalServerUrls.filter((img: string) => !base64Images.includes(img));

        const newItem: SparkPlugItem = {
          id: initialValues?.id || crypto.randomUUID(),
          key: initialValues?.key || Date.now().toString(),
          ...values,
          images: base64Images,
          references: refList,
          oems: oemList,
          _deletedImgs,
        } as any;

        await onAdd(newItem);
        message.success('Товар добавлен или обновлён!');
        resetForm();
      } catch {
        message.error('Заполните все обязательные поля');
      }
    },
    [form, refList, oemList, onAdd, initialValues, resetForm]
  );

  return (
    <Form
      form={form}
      name="SparkPlugForm"
      layout="vertical"
      preserve={false}
      onFinish={handleFinish}
    >
      <Form.Item name="brand" label="Бренд" rules={[{ required: true }]}>
        <Select showSearch optionFilterProp="children" onChange={handleBrandSelect}>
          <Option value="NGK">NGK</Option>
          <Option value="Denso">Denso</Option>
          <Option value="Bosch">Bosch</Option>
          <Option value="Champion">Champion</Option>
          <Option value="Motorcraft">Motorcraft</Option>
          <Option value="ACDelco">ACDelco</Option>
        </Select>
      </Form.Item>

      <Form.Item name="partNumber" label="Номер детали" rules={[{ required: true }]}>
        <Input />
      </Form.Item>

      <Form.Item name="sku" label="SKU">
        <Input maxLength={10} />
      </Form.Item>

      <Form.Item name="model" label="Модель" rules={[{ required: true }]}>
        <Input />
      </Form.Item>

      <Form.Item name="price" label="Цена" rules={[{ required: true }, nonNegativeRule('Цена')]}>
        <Input type="number" />
      </Form.Item>

      <Form.Item name="quantity" label="Количество" rules={[{ required: true }, nonNegativeRule('Количество')]}>
        <Input type="number" />
      </Form.Item>

      <Form.Item name="electrodeType" label="Тип электрода" rules={[{ required: true }]}>
        <Select>
          <Option value="рутений">рутений</Option>
          <Option value="дубль иридий">дубль иридий</Option>
          <Option value="иридий">иридий</Option>
          <Option value="дубль платина">дубль платина</Option>
          <Option value="платина">платина</Option>
          <Option value="никель-медь">никель-медь</Option>
          <Option value="медь">медь</Option>
        </Select>
      </Form.Item>

      <Form.Item name="electrodeCount" label="Кол-во электродов" rules={[{ required: true }, nonNegativeRule('Кол-во электродов')]}>
        <Select>
          {[1, 2, 3, 4].map((n) => (
            <Option key={n} value={n}>
              {n}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item name="hexType" label="Тип ключа" rules={[{ required: true }]}>
        <Select>
          <Option value="шестигранник">шестигранник</Option>
          <Option value="многогранник">многогранник</Option>
        </Select>
      </Form.Item>

      <Form.Item name="hexSize" label="Размер ключа" rules={[{ required: true }]}>
        <Select>
          {['12мм', '14мм', '16мм', '21мм'].map((size) => (
            <Option key={size} value={size}>
              {size}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item name="seatType" label="Седло" rules={[{ required: true }]}>
        <Select>
          <Option value="шайба">шайба</Option>
          <Option value="конус">конус</Option>
        </Select>
      </Form.Item>

      <Form.Item name="threadSize" label="Размер резьбы" rules={[{ required: true }]}>
        <Select>
          {['10мм', '12мм', '14мм'].map((size) => (
            <Option key={size} value={size}>
              {size}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item name="threadLength" label="Длина резьбы" rules={[{ required: true }]}>
        <Select>
          {['11.2мм', '12.7мм', '17.5мм', '19мм', '20.5мм', '22мм', '25мм', '26.5мм', '28.5мм'].map((size) => (
            <Option key={size} value={size}>
              {size}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item name="gap" label="Зазор (мм)" rules={[{ required: true }, nonNegativeRule('Зазор')]}>
        <Input />
      </Form.Item>

      <Form.Item
        name="heatRating"
        label="Калильное число"
        rules={heatByCross ? [] : [{ required: true }, nonNegativeRule('Калильное число')]}
        style={brand === 'ACDelco' ? { marginBottom: 8 } : undefined}
      >
        <Input disabled={heatByCross} placeholder={heatByCross ? 'не вводится — на сайте «сверьте по аналогам от других производителей»' : undefined} />
      </Form.Item>

      {brand === 'ACDelco' && (
        <Form.Item>
          <Checkbox checked={heatByCross} onChange={(e) => toggleHeatByCross(e.target.checked)}>
            ACDelco — калильное не вводить, на сайте будет «сверьте по аналогам от других производителей»
          </Checkbox>
        </Form.Item>
      )}

      <Form.Item name="original" label="Тип товара" rules={[{ required: true }]}>
        <Select>
          <Option value="original">Оригинал</Option>
          <Option value="copy">Копия</Option>
        </Select>
      </Form.Item>

      {/* Если ImageUploader не сам заполняет поле формы "images",
          оберни его так (и добавь нормалайзер события под твой аплоадер):
          <Form.Item name="images" valuePropName="fileList" getValueFromEvent={normUpload}>
            <ImageUploader />
          </Form.Item>
      */}
      <ImageUploader />

      <RefOemManager title="REF" data={refList} setData={setRefList} />
      <RefOemManager title="OEM" data={oemList} setData={setOemList} />

      <div className="form-actions-row">
        <Button onClick={resetForm}>Очистить форму</Button>
        <Button type="primary" htmlType="submit">
          Добавить продукт
        </Button>
      </div>
    </Form>
  );
};

export default SparkPlugForm;
